import { IEnumResponse } from "../../interfaces/i-enum";
import { EnumMapType } from "./enum-map-type";

export class EnumMap {
    private _enumJson: IEnumResponse[];
    // String is to allow for c# long numbers, which number doesn't support.
    private _enumMapByValue: EnumMapType = new Map<number | string, string>();
    private _enumMapByName: EnumMapType = new Map<string, string | number>();
    private _enumMapObjectByValue: Map<string | number, IEnumResponse> = new Map();
    private _enumMapObjectByName: Map<string | number, IEnumResponse> = new Map();

    public constructor(enumJson: IEnumResponse[]) {
        this._enumJson = enumJson;
        this.constructEnumMap();
        return this;
    }

    private constructEnumMap() {
        this._enumJson.forEach(item => {
            this._enumMapByValue.set(item.value, item.name);
            this._enumMapByName.set(item.name.toLocaleLowerCase(), item.value);
            this._enumMapObjectByValue.set(item.value, item);
            this._enumMapObjectByName.set(item.name.toLocaleLowerCase(), item);
        });
    }

    public getMap() {
        return this._enumMapByValue;
    }

    // This will check both the string and number version of the key. In some places the number is recieved as a string, so this solves for casting in each source
    public getFromMap(map: Map<string | number, string | number | IEnumResponse>, key: string | number) {
        let result = map.get(key);
        if (result !== undefined) {
            return result;
        }

        if (typeof key === "string") {
            let numberValue = Number(key);
            return !isNaN(numberValue) ? map.get(numberValue) : "";
        }

        return map.get(key?.toString()) ?? "";
    }

    public getEnumName(value: string | number) {
        if (this._enumMapByValue.size > 0) {
            return this.getFromMap(this._enumMapByValue, value) as string;
        }
        return "";
    }

    public getEnumValue(name: string) {
        if (this._enumMapByName.size > 0) {
            return this.getFromMap(this._enumMapByName, name?.toString().toLocaleLowerCase()) as string | number;
        }
        return null;
    }

    public getEnumObjectByValue(value: string | number) {
        if (this._enumMapObjectByValue.size > 0) {
            return this.getFromMap(this._enumMapObjectByValue, value) as IEnumResponse;
        }
        return null;
    }

    public getEnumObjectByName(name: string) {
        if (this._enumMapObjectByName.size > 0) {
            return this.getFromMap(this._enumMapObjectByName, name.toString().toLocaleLowerCase()) as IEnumResponse;
        }
        return null;
    }

    public checkEnumName(enumName: string, nameToTest: string) {
        // Just gets alphabets
        let onlyAlphabets = enumName.replace(/[^A-Za-z ]/g, "").toLowerCase();
        return onlyAlphabets.includes(nameToTest);
    }

    public checkEnumValue(enumName: string, value: string | number) {
        return this.getEnumValue(enumName) === value;
    }
}

// Does not currently support Long/BigInt enums, like Credentials
export class EnumComparer {
    // Accepts any so it can support either string or number enums, and also combined value or list of individual values
    public static doFlagsOverlap(sourceValue: any, targetValue: any) {
        let sourceValueAsNumber: number;
        if (Array.isArray(sourceValue)) {
            sourceValueAsNumber = this.combineFlags(sourceValue);
        } else {
            sourceValueAsNumber = Number(sourceValue);
        }

        let targetValueAsNumber: number;
        if (Array.isArray(targetValue)) {
            targetValueAsNumber = this.combineFlags(targetValue);
        } else {
            targetValueAsNumber = Number(targetValue);
        }

        return (sourceValueAsNumber & targetValueAsNumber) !== 0;
    }

    public static combineFlags(values: any[]): number {
        return values.reduce((combinedValue, currentValue) => {
            return combinedValue | Number(currentValue);
        }, 0);
    }
}
